import { useState, type ReactNode } from 'react';
import { home3AgentName, homeFileKey, formatFeedTime, type Home3Event } from '../../../lib/home3-feed';
import { useTranslation } from '../../../lib/i18n';
import { useAgentWorkspaceFiles } from '../../../lib/agent-workspace-files';
import { openFileForRow } from '../../../lib/doc-viewer-presenter';
import { useWorkspaceGoals } from '../../../lib/goals';
import { useRoutines } from '../../../lib/routines';
import { useBasePath } from '../../../lib/use-base-path';
import { useAgentName } from '../../../lib/use-agent-name';
import type { WebTask } from '../../../lib/tasks';
import type { SentOutreachSummary } from '../../../lib/outreach-rows';
import { fileNewTabLink } from '../FileArtifactCard';
import { LiveGoalDetailsModal } from '../goals/GoalDetailsModal';
import { RoutineDetailModal } from '../routines/RoutineDetailModal';
import { Modal } from '../../Modal';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { TaskDetailModal } from '../tasks/TaskDetailModal';
import { OutreachThreadModal, receivedThreadSibling } from '../tasks/OutreachThreadModal';
import type { ThreadSibling } from '../outreach/ThreadStepper';
import { Badge, Typography } from '../ui';
import { OutreachCardModal } from './OutreachCardModal';
import { useOutreachCardContext } from './use-outreach-card-context';
import { HOME3_PAGE } from './Home3Feed';
import styles from './Home3Feed.module.css';

/**
 * What a feed row OPENS — one rule for every surface that lists the Overview's events, so a row cannot open
 * one thing on the board and another in the canvas's activity panel.
 *
 * A row backed by a task pushes the shared `TaskDetailModal` onto `TaskSidePanelContext`. A MESSAGES row
 * opens its thread (`OutreachThreadModal`, or the outreach card for one the agent sent) — it is not a task,
 * and the generic detail has nothing to say about one. A goal, routine or file row opens that item's own
 * drill-in. Only a row with none of those (an app an agent built) falls back to a small modal of its own.
 *
 * `modals` is what the opener draws itself (the routine drill-in and that fallback): render it once, in the
 * surface that calls `open`. `threadSibling` is what a thread dialog's Previous / Next steps through.
 */
export function useHome3EventOpener({ agentNames, spaceNames }: { agentNames: Record<string, string>; spaceNames: Record<string, string> }): {
  open: (event: Home3Event, siblings?: readonly ThreadSibling[]) => void;
  threadSibling: (event: Home3Event) => ThreadSibling | undefined;
  modals: ReactNode;
} {
  const { t } = useTranslation();
  const maestro = useAgentName();
  const { openModal, closeModal } = useTaskSidePanel();
  const [opened, setOpened] = useState<Home3Event | null>(null);
  // Who a Messages card is with and where its actions go (contact book + profile-link leads).
  const cardContext = useOutreachCardContext();
  // An ITEM row opens the item's own drill-in — the one its screen opens. These reads are the same cache
  // entries `Home3Body` built the rows from, so the lookup costs nothing.
  const basePath = useBasePath();
  const goalsState = useWorkspaceGoals();
  const { state: routinesState } = useRoutines();
  const filesState = useAgentWorkspaceFiles();
  const [openRoutineId, setOpenRoutineId] = useState<string | null>(null);
  const openRoutine =
    openRoutineId && routinesState.kind === 'ready' ? routinesState.routines.find((r) => r.id === openRoutineId) : undefined;

  /** Opens an item row. Returns false when the item is not loaded, so the caller falls back. */
  const openItem = (event: Home3Event): boolean => {
    const item = event.item;
    if (!item) return false;
    if (item.kind === 'goal') {
      const goal = goalsState.status === 'ready' ? goalsState.goals.find((g) => g.id === item.id) : undefined;
      if (!goal) return false;
      openModal(<LiveGoalDetailsModal workspaceId={goal.workspaceId} goalId={goal.id} initialGoal={goal} />, goal.id);
      return true;
    }
    if (item.kind === 'routine') {
      if (routinesState.kind !== 'ready' || !routinesState.routines.some((r) => r.id === item.id)) return false;
      setOpenRoutineId(item.id);
      return true;
    }
    const file = filesState.status === 'ready' ? filesState.items.find((f) => homeFileKey(f) === item.id) : undefined;
    if (!file) return false;
    const fileHref = file.workspaceSlug ? fileNewTabLink(file.workspaceSlug, file, basePath) : undefined;
    openFileForRow(file, HOME3_PAGE, fileHref, file.workspaceName);
    return true;
  };

  const threadSibling = (event: Home3Event): ThreadSibling | undefined => {
    if (event.outreach?.kind === 'inbound') return receivedThreadSibling(event.outreach.task as WebTask);
    if (event.outreach?.kind === 'sent') {
      const summary = event.outreach.thread as SentOutreachSummary;
      return {
        id: summary.id,
        render: (siblings) => (
          <OutreachCardModal summary={summary} ctx={cardContext(summary.id)} onClose={closeModal} page={HOME3_PAGE} siblings={siblings} />
        ),
      };
    }
    return undefined;
  };

  const open = (event: Home3Event, siblings: readonly ThreadSibling[] = []) => {
    if (event.outreach?.kind === 'inbound') {
      openModal(<OutreachThreadModal task={event.outreach.task as WebTask} siblings={siblings} />, event.id);
    } else if (event.outreach?.kind === 'sent') {
      // The outreach CARD's detail: the person, where they came from, the true status, the conversation
      // and its actions (Ronny, 2026-10-22).
      const summary = event.outreach.thread as SentOutreachSummary;
      openModal(
        <OutreachCardModal summary={summary} ctx={cardContext(summary.id)} onClose={closeModal} page={HOME3_PAGE} siblings={siblings} />,
        event.id,
      );
    } else if (event.taskId) {
      openModal(<TaskDetailModal taskId={event.taskId} />, event.taskId);
    } else if (!openItem(event)) {
      setOpened(event);
    }
  };

  const modals = (
    <>
      {openRoutine && (
        <RoutineDetailModal
          key={openRoutine.id}
          routine={openRoutine}
          spaceName={openRoutine.workspaceId ? spaceNames[openRoutine.workspaceId] : undefined}
          onClose={() => setOpenRoutineId(null)}
        />
      )}

      {/* Only for a row with no drill-in of its own. */}
      <Modal open={!!opened} onClose={() => setOpened(null)} title={opened?.title} maxHeightPx={650} scrollBody>
        {opened && (
          <div className={styles.body}>
            <Typography variant="p3" color="secondary">
              {formatFeedTime(opened.at)}
            </Typography>
            <Typography variant="p2" color="secondary" className={styles.subRow}>
              {opened.subtitle.kind === 'agent'
                ? home3AgentName(opened, agentNames)
                : opened.subtitle.kind === 'contact'
                  ? [opened.subtitle.name, opened.subtitle.channel].filter(Boolean).join(' | ')
                  : opened.subtitle.kind === 'maestro'
                    ? maestro.short
                    : ''}
              <Badge variant={opened.badge.variant} size="tiny" label={t(opened.badge.labelKey)} />
            </Typography>
          </div>
        )}
      </Modal>
    </>
  );

  return { open, threadSibling, modals };
}
